import React, { memo, useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Spinner from "../Spinner";
import PulseScanResultRow from "./rows/PulseScanResultRow";
import { usePulseScanResults } from "../../hooks/results/usePulseScanResults";
import MetricTrendChart from "../charts/MetricTrendChart";
import { getValidatedMeasurementValue } from "../../utils/results/validatedRanges";
import {
  DEFAULT_RESULTS_PAGE_SIZE,
  useInfiniteResultsPageSize,
} from "../../hooks/results/useInfiniteResultsPageSize";

function toLocalDayKey(ms) {
  const d = new Date(ms);
  // local YYYY-MM-DD
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function labelDdMm(ms) {
  const d = new Date(ms);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}`;
}

export default memo(function PulseScanResults({ db, uid, subjectId }) {
  const navigate = useNavigate();
  const [pageSize, setPageSize] = useState(DEFAULT_RESULTS_PAGE_SIZE);
  const resetKey = `${uid || ""}:${subjectId || ""}:pulse`;
  const { rows, loading, loadingMore, hasMore, error } = usePulseScanResults(
    db,
    uid,
    {
      pageSize,
      subjectId,
    },
  );
  const { handleScroll } = useInfiniteResultsPageSize({
    canLoadMore: hasMore,
    loading,
    loadingMore,
    rowsLength: rows.length,
    resetKey,
    pageSize,
    setPageSize,
    initialPageSize: DEFAULT_RESULTS_PAGE_SIZE,
  });

  const context = "pulse"; // deterministic routing

  const onRowClick = useCallback(
    (row) => {
      const variant = "RecordingTidalBreathing";
      const docid = row?.id;
      if (!docid) return;

      const qs =
        `variant=${encodeURIComponent(variant)}` +
        `&docid=${encodeURIComponent(docid)}` +
        `&context=${encodeURIComponent(context)}`;

      navigate(`/result?${qs}`); // NO replace; allow back navigation
    },
    [navigate],
  );

  // -------- Trend prep (generic: xValues + values + daily min/max + counts)
  const trend = useMemo(() => {
    if (!Array.isArray(rows) || !rows.length) {
      return {
        labels: [],
        xValues: [],
        values: [],
        minMax: [],
        counts: [],
        currentValue: null,
        currentIndex: null,
      };
    }

    // rows is DESC (newest first) -> reverse for chronological processing
    const chron = [...rows].reverse();

    // Build per-recording tuples with valid ms + hr
    const samples = [];
    for (const r of chron) {
      const ms = r?.timestamp ? Date.parse(r.timestamp) : NaN;
      const v = getValidatedMeasurementValue("hr", Number(r?.hr));
      if (!Number.isFinite(ms)) continue;
      if (!Number.isFinite(v)) continue;
      samples.push({ ms, v });
    }

    // Group by local day
    const byDay = new Map(); // dayKey -> { dayStartMs, sum, n, min, max }
    for (const s of samples) {
      const d = new Date(s.ms);
      const dayKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0",
      )}-${String(d.getDate()).padStart(2, "0")}`;

      // local midnight (for stable x position)
      const dayStart = new Date(
        d.getFullYear(),
        d.getMonth(),
        d.getDate(),
      ).getTime();

      const cur = byDay.get(dayKey) || {
        dayStartMs: dayStart,
        sum: 0,
        n: 0,
        min: s.v,
        max: s.v,
      };

      cur.sum += s.v;
      cur.n += 1;
      cur.min = Math.min(cur.min, s.v);
      cur.max = Math.max(cur.max, s.v);

      byDay.set(dayKey, cur);
    }

    // Sort days chronologically
    const days = Array.from(byDay.values()).sort(
      (a, b) => a.dayStartMs - b.dayStartMs,
    );

    const xValues = days.map((d) => d.dayStartMs);
    const values = days.map((d) => d.sum / d.n); // daily average
    const minMax = days.map((d) => ({ min: d.min, max: d.max }));
    const counts = days.map((d) => d.n);

    const labels = xValues.map((ms) => {
      const dt = new Date(ms);
      const dd = String(dt.getDate()).padStart(2, "0");
      const mm = String(dt.getMonth() + 1).padStart(2, "0");
      return `${dd}/${mm}`;
    });

    const currentIndex = values.length ? values.length - 1 : null;
    const currentValue = currentIndex != null ? values[currentIndex] : null;

    return {
      labels,
      xValues,
      values,
      minMax,
      counts,
      currentValue,
      currentIndex,
    };
  }, [rows]);

  // -------- UI states
  if (!uid || loading)
    return (
      <div className="min-h-64 flex items-center justify-center">
        <Spinner />
      </div>
    );

  if (error) return <div className="muted-text">Failed to load results.</div>;
  if (!rows.length) return <div className="muted-text">No results yet.</div>;

  return (
    <div className="w-full h-full min-h-0 flex flex-col gap-2">
      {/* Trend chart */}
      {/* <MetricTrendChart
        height={190}
        labels={trend.labels}
        values={trend.values}
        xValues={trend.xValues}
        xTickFormat="dd/MM"
        lineLabel="HR (Avg)"
        yUnit="bpm"
        yDecimals={0}
        showMinMaxBars={true}
        minMax={trend.minMax}
        counts={trend.counts}
        currentValue={trend.currentValue}
        currentIndex={trend.currentIndex}
      /> */}

      {/* scrolling list */}
      <div
        className="w-full flex-1 min-h-0 overflow-y-auto"
        onScroll={handleScroll}
      >
        {rows.map((r, idx) => (
          <PulseScanResultRow
            key={r.id ?? idx}
            time={r.time}
            date={r.date}
            hr={r.hr}
            hrQuality={r.hrQuality}
            onClick={() => onRowClick(r)}
          />
        ))}
        {loadingMore ? <Spinner size={24} className="py-4" /> : null}
      </div>
    </div>
  );
});
